import { ContextConsumer } from '@lit/context'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { comboboxRegistryContext } from './combobox-context.js'

/**
 * A declarative option for a [Combobox](/components/combobox/). Place these as
 * children of `<nord-combobox>` instead of (or as an alternative to) setting the
 * `.options` property:
 *
 * ```html
 * <nord-combobox>
 *   <nord-combobox-option value="bug">Bug</nord-combobox-option>
 *   <nord-combobox-option value="feature">Feature</nord-combobox-option>
 * </nord-combobox>
 * ```
 *
 * The option's child markup becomes the rendered row, so you can author custom
 * items (e.g. an avatar + text) without an `html` string or a render function.
 * Set the `label` attribute when the visible markup isn't plain text, so
 * filtering and the accessible name have something to match on.
 *
 * The element renders nothing itself — it is a data source the combobox reads via
 * context and is hidden with `display: none`.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot - The option's label / custom content.
 */
@customElement('nord-combobox-option')
export default class ComboboxOption extends LitElement {
  /** The value submitted with the form and tracked in the combobox's `value`. */
  @property({ reflect: true }) value = ''

  /** Whether the option is selectable. */
  @property({ reflect: true, type: Boolean }) disabled = false

  /** Text used for filtering and the accessible name. Defaults to the text content. */
  @property({ reflect: true }) label?: string

  private registry = new ContextConsumer(this, {
    context: comboboxRegistryContext,
    subscribe: true,
    // Register once the combobox's registry resolves (covers late upgrades).
    callback: registry => registry?.add(this),
  })

  /** Re-sync when the option's child content (custom markup) changes. */
  private contentObserver?: MutationObserver

  protected createRenderRoot() {
    return this
  }

  connectedCallback() {
    super.connectedCallback()
    this.registry.value?.add(this)
    this.contentObserver ??= new MutationObserver(() => this.registry.value?.notify())
    this.contentObserver.observe(this, { childList: true, characterData: true, subtree: true })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.registry.value?.remove(this)
    this.contentObserver?.disconnect()
    this.contentObserver = undefined
  }

  protected updated() {
    // value / disabled / label changed — re-sync with the combobox.
    this.registry.value?.notify()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-combobox-option': ComboboxOption
  }
}
